import React, { useState, useEffect, useMemo } from 'react'
import { Box, Typography, CircularProgress, Card, CardContent, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  ScatterPlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsReferenceLine,
  ChartsTooltip,
  ResponsiveChartContainer,
} from '@mui/x-charts'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'

interface TradeDurationChartProps {
  selectedGroup: string
  backtestId?: string
  alertId?: string
}

interface TradeData {
  id: string
  entryTime: number
  exitTime?: number
  profit: number
  status: string
  group: string
  durationHours?: number
}

interface ChartDataPoint {
  x: number // duration in hours
  y: number // profit
  id: string
}

const TradeDurationChart: React.FC<TradeDurationChartProps> = ({
  selectedGroup,
  backtestId,
  alertId,
}) => {
  const theme = useTheme()
  const { canViewBacktestInsights } = useApplicationSuiteContext()

  const [trades, setTrades] = useState<TradeData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [getBacktestTrades] = useHttpsCallable(firebase_functions, 'getBacktestTrades')

  // Fetch trades for selected group
  useEffect(() => {
    if (!backtestId && !alertId) return
    if ((backtestId && !selectedGroup) || !canViewBacktestInsights) return

    const fetchTrades = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const queryParams = backtestId ? { backtestId, tradeGroup: selectedGroup } : { alertId }
        const result: any = await getBacktestTrades(queryParams)

        if (result?.data?.data) {
          const tradesData: TradeData[] = result.data.data
            .filter(
              (trade: any) =>
                (trade.status === 'CLOSED' || trade.status === 'closed') &&
                trade.exitTime &&
                trade.entryTime
            )
            .map((trade: any) => {
              const durationMs = trade.exitTime - trade.entryTime
              const durationHours = Number((durationMs / (1000 * 60 * 60)).toFixed(2)) // Convert to hours

              return {
                id: trade.id || `${trade.entryTime}-${trade.exitTime}`,
                entryTime: trade.entryTime,
                exitTime: trade.exitTime,
                profit: Number(trade.profit.toFixed(2)) || 0,
                status: trade.status,
                group: trade.group,
                durationHours,
              }
            })

          setTrades(tradesData)
        } else {
          setTrades([])
        }
      } catch (err) {
        console.error('Error fetching trades:', err)
        setError('Failed to load trade data')
        setTrades([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchTrades()
  }, [selectedGroup, backtestId, alertId, getBacktestTrades, canViewBacktestInsights])

  // Transform trades into chart data, separated by profit/loss
  const { profitableTradesData, losingTradesData } = useMemo(() => {
    const profitableTradesData: ChartDataPoint[] = []
    const losingTradesData: ChartDataPoint[] = []

    trades.forEach((trade) => {
      const dataPoint = {
        x: trade.durationHours || 0,
        y: trade.profit,
        id: trade.id,
      }

      if (trade.profit >= 0) {
        profitableTradesData.push(dataPoint)
      } else {
        losingTradesData.push(dataPoint)
      }
    })

    return { profitableTradesData, losingTradesData }
  }, [trades])

  // Calculate statistics
  const statistics = useMemo(() => {
    if (trades.length === 0) return null

    const profitableTrades = trades.filter((trade) => trade.profit > 0)
    const losingTrades = trades.filter((trade) => trade.profit < 0)

    const avgDuration =
      trades.reduce((sum, trade) => sum + (trade.durationHours || 0), 0) / trades.length
    const avgProfitableDuration =
      profitableTrades.length > 0
        ? profitableTrades.reduce((sum, trade) => sum + (trade.durationHours || 0), 0) /
          profitableTrades.length
        : 0
    const avgLosingDuration =
      losingTrades.length > 0
        ? losingTrades.reduce((sum, trade) => sum + (trade.durationHours || 0), 0) /
          losingTrades.length
        : 0

    return {
      totalTrades: trades.length,
      profitableTrades: profitableTrades.length,
      losingTrades: losingTrades.length,
      avgDuration: avgDuration,
      avgProfitableDuration: avgProfitableDuration,
      avgLosingDuration: avgLosingDuration,
      maxProfit: Math.max(...trades.map((t) => t.profit)),
      maxLoss: Math.min(...trades.map((t) => t.profit)),
      longestTrade: Math.max(...trades.map((t) => t.durationHours || 0)),
      shortestTrade: Math.min(...trades.map((t) => t.durationHours || 0)),
    }
  }, [trades])

  const formatDuration = (hours: number): string => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`
    } else if (hours < 24) {
      return `${hours.toFixed(1)}h`
    } else {
      const days = Math.floor(hours / 24)
      const remainingHours = Math.round(hours % 24)
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
    }
  }

  const renderContent = () => (
    <Box>
      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Chart */}
      {!isLoading && !error && trades.length > 0 && (
        <>
          <Grid container spacing={2}>
            {/* Statistics */}
            {statistics && (
              <Grid item xs={12}>
                <Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Total Trades
                      </Typography>
                      <Typography variant="h6">{statistics.totalTrades}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Average Duration
                      </Typography>
                      <Typography variant="h6">{formatDuration(statistics.avgDuration)}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Profitable Trades Avg Duration
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatDuration(statistics.avgProfitableDuration)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Losing Trades Avg Duration
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {formatDuration(statistics.avgLosingDuration)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Duration Range
                      </Typography>
                      <Typography variant="h6">
                        {formatDuration(statistics.shortestTrade)} -{' '}
                        {formatDuration(statistics.longestTrade)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Profit Range
                      </Typography>
                      <Typography variant="h6">
                        ${statistics.maxLoss.toFixed(2)} to ${statistics.maxProfit.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} lg={6}>
              <Card elevation={0} sx={{ backgroundColor: theme.palette.background.custom3, mb: 3 }}>
                <CardContent>
                  <Typography variant="h6">Duration vs Profitability</Typography>

                  <Box sx={{ height: 300, width: '100%' }}>
                    <ResponsiveChartContainer
                      height={300}
                      series={[
                        ...(profitableTradesData.length > 0
                          ? [
                              {
                                data: profitableTradesData,
                                label: 'Profitable Trades',
                                color: theme.palette.success.main,
                                type: 'scatter' as const,
                              },
                            ]
                          : []),
                        ...(losingTradesData.length > 0
                          ? [
                              {
                                data: losingTradesData,
                                label: 'Losing Trades',
                                color: theme.palette.error.main,
                                type: 'scatter' as const,
                              },
                            ]
                          : []),
                      ]}
                      xAxis={[
                        {
                          label: 'Duration (hours)',
                        },
                      ]}
                      yAxis={[
                        {
                          label: 'Profit ($)',
                        },
                      ]}
                    >
                      <ChartsGrid vertical horizontal />
                      <ScatterPlot />
                      <ChartsReferenceLine
                        y={0}
                        lineStyle={{
                          stroke: theme.palette.grey[400],
                          strokeWidth: 1,
                          strokeDasharray: '5,5',
                        }}
                      />
                      <ChartsXAxis />
                      <ChartsYAxis />
                      <ChartsTooltip trigger="item" />
                    </ResponsiveChartContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* No Data State */}
      {!isLoading &&
        !error &&
        trades.length === 0 &&
        ((backtestId && selectedGroup) || alertId) && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No closed trades found for the selected ratio group.
            </Typography>
          </Box>
        )}
    </Box>
  )

  if (backtestId && !selectedGroup) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          Select a trade ratio to view duration analysis.
        </Typography>
      </Box>
    )
  }

  return renderContent()
}

export default TradeDurationChart
